import FormFieldErrors from "@/app/components/ui/Reusable/FormFieldErrors";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useAddressSearchAPI } from "@/app/hooks/api_hooks/HDCart/useAddressSearchAPI";
import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useFormUtils } from "@/app/hooks/useFormUtils";
import { useLoader } from "@/app/hooks/useLoader";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, InformationCircleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { Fragment, useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";

const isValidAddressQuery = (query: string): boolean => {
  if (!query?.trim()) return false;
  const addressRegex = /^[a-zA-Z0-9\s.,\-'#/&()]+$/;
  return addressRegex.test(query.trim());
};

const AddressSearchComponent = ({
  errorMessages,
  onSelectItem,
  className,
  itemsClassName,
  register,
  fieldError,
  listBoxClassName,
  showTypedErrors,
  showDropdownWhenNoData,
  showTooltips,
  ...props
}) => {
  const { deduceFormFieldErrors, isFormFieldValid } = useFormUtils();
  const [preFilledFormattedAddress, setPrefilledFormattedAddress] = useState<string | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<ISelectMenuItemData | null>(null);
  const [query, setQuery] = useState("");
  const { geoAddress, execute: searchLocation, clearGeoSearch } = useAddressSearchAPI();
  const isGeoLocationSearchInProgress = useLoader("isGeoLocationSearchInProgress");
  const debouncedSearch = useDebounce(query, 400);

  useAfterFirstRender(() => {
    if (!isValidAddressQuery(query)) {
      clearGeoSearch();
      return;
    }
    searchLocation(query);
  }, [debouncedSearch]);

  const filteredGeoData = useSelectMenuReducer(geoAddress, "formattedAddress", "placeId");

  const reducedErrorMessages = deduceFormFieldErrors(errorMessages, fieldError);

  const isValid = isFormFieldValid(reducedErrorMessages);

  const showFieldErrors = showTypedErrors && showTypedErrors === true ? true : showTypedErrors === false ? false : true;

  const handleAddressItemSelect = (item: ISelectMenuItemData) => {
    setSelectedAddress(item);
    if (onSelectItem) {
      const selectedAddress = geoAddress?.filter((ga) => ga.placeId === item.id);
      onSelectItem({
        ...item,
        state: selectedAddress[0] ?? {},
      });
    }
  };

  const resetInput = () => {
    setSelectedAddress(null);
    clearGeoSearch();
    if (props?.onClear) {
      props?.onClear();
    }
    setPrefilledFormattedAddress(null);
  };

  useEffect(() => {
    if (props?.clearSearch === true) {
      setSelectedAddress(null);
      setQuery(null);
      clearGeoSearch();
    }
  }, [props?.clearSearch]);

  useEffect(() => {
    if (props?.defaultAddressString && props?.defaultAddressString !== "") {
      setPrefilledFormattedAddress(props?.defaultAddressString);
    }
  }, [props?.defaultAddressString]);

  return (
    <div style={props?.style ?? {}} className={clsx("relative", props?.containerClassName ?? "")}>
      <Combobox
        disabled={props?.disabled && props?.disabled === true ? true : false}
        value={selectedAddress}
        onChange={(item: ISelectMenuItemData) => {
          handleAddressItemSelect(item);
        }}
        nullable
      >
        {props?.label && (
          <span className="-top-5 left-1 absolute flex justify-start items-center text-gray-500 text-xs">
            {props?.label}{" "}
            {props?.labelInfo && (
              <span
                data-tooltip-content={props?.labelInfo}
                data-tooltip-id={`${register.name}:label_info`}
                className="ml-1"
              >
                {" "}
                <InformationCircleIcon className="inline w-4 h-4" />{" "}
                <Tooltip id={`${register.name}:label_info`} style={{ fontSize: "10px" }} />
              </span>
            )}
          </span>
        )}
        <div className="relative">
          <div className="relative border-gray-300 bg-white border rounded-lg text-left sm:text-sm cursor-default overflow-hidden focus:outline-none focus-visible:ring-1 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300">
            <Combobox.Button className={"w-full"}>
              <Combobox.Input
                className={clsx(
                  `w-full text-left text-xs font-medium  rounded-lg py-2 pl-3 pr-10 text-gray-500 leading-5  focus:ring-0 ${className}`,
                  isValid
                    ? "text-gray-500 border-none outline-none font-medium"
                    : "text-red-600  border border-red-500  outline-red-500 font-medium",
                  props?.disabled && props?.disabled === true && "bg-gray-200",
                )}
                displayValue={(item: any) =>
                  item ? item?.title : preFilledFormattedAddress ? preFilledFormattedAddress : ""
                }
                placeholder={props?.placeholder}
                //   displayValue={(item: any) => item.title}
                onChange={(event) => setQuery(event.target.value)}
                autoComplete="off"
                autoCorrect="false"
              />
            </Combobox.Button>
            {((props?.withClearAction && selectedAddress) || preFilledFormattedAddress) && (
              <span
                onClick={() => {
                  resetInput();
                }}
                className="right-9 absolute p-2 h-full translate-x-1/2 cursor-pointer"
              >
                <XMarkIcon className="w-5 h-5 text-red-500" />
              </span>
            )}
            <Combobox.Button className="right-0 absolute inset-y-0 flex items-center pr-2">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          {showFieldErrors && (
            <div className="">
              <FormFieldErrors errors={reducedErrorMessages} />
            </div>
          )}
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <div className="absolute border-gray-50 bg-white shadow-md mt-2 border rounded-md w-full max-h-60">
              <Combobox.Options
                style={{ zIndex: 1 }}
                className={clsx(
                  filteredGeoData?.length > 0 &&
                    "absolute  mt-1  overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none max-h-60 sm:text-sm",
                  listBoxClassName ? listBoxClassName : "w-full",
                )}
              >
                {isGeoLocationSearchInProgress && (
                  <div className="z-20 border-gray-100 bg-white p-2 border text-center text-gray-400 text-xs">
                    Searching...
                  </div>
                )}
                {filteredGeoData?.length === 0
                  ? showDropdownWhenNoData &&
                    showDropdownWhenNoData === true &&
                    query?.length > 0 && (
                      <div className="relative z-20 border-gray-100 bg-white px-4 py-2 border text-gray-700 text-xs cursor-default select-none">
                        {query?.length > 0 ? "Try searching for different" : "Try Searching First"}
                      </div>
                    )
                  : filteredGeoData?.map((item: ISelectMenuItemData) => (
                      <Combobox.Option
                        key={item.id}
                        className={({ selected: _s }) =>
                          clsx(
                            "relative flex font-medium  pl-2 text-xs justify-start items-center cursor-default select-none py-2 text-center ",
                            selectedAddress?.id === item.id
                              ? "bg-gray-800 hover:bg-gray-800 text-white font-medium "
                              : "text-gray-800 hover:bg-gray-800 hover:text-white",
                            itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                          )
                        }
                        onClick={() => setSelectedAddress(item)}
                        data-tooltip-place="right"
                        data-tooltip-id={`option-tooltip:${register?.name}`}
                        data-tooltip-content={item.title}
                        value={item}
                      >
                        {({ selected: _l, active }) => (
                          <>
                            <span>
                              <MapPinIcon className="w-4 h-4" />
                            </span>
                            <span className={`block text-left ml-2 truncate `}>{item.title}</span>
                            {selectedAddress?.id === item?.id ? (
                              <div className="flex justify-start items-center text-left">
                                <span
                                  className={`inset-y-0 text-left left-0 flex items-center pl-3 ${
                                    active ? "text-white" : "text-blue-600"
                                  }`}
                                ></span>
                                <CheckIcon className="mr-4 w-4 h-4" />
                              </div>
                            ) : null}
                          </>
                        )}
                      </Combobox.Option>
                    ))}
              </Combobox.Options>
            </div>
          </Transition>
        </div>
      </Combobox>
      {showTooltips && showTooltips === true && (
        <Tooltip id={`option-tooltip:${register?.name}`} style={{ zIndex: 5, fontSize: "10px" }} />
      )}
    </div>
  );
};

export default AddressSearchComponent;
